import { evaluateFeatureFlags } from "@/services/api/featureFlagService"
import { FeatureFlagKey, FeatureFlagMap } from "@/types/featureFlag"
import { DEFAULT_FLAGS } from "@/utils/featureFlags"
import { setFlagRefreshCallback, setFlagStore } from "@/utils/featureFlagStore"
import React, { createContext, useCallback, useContext, useState } from "react"

interface FeatureFlagContextType {
  flags: FeatureFlagMap
  isLoading: boolean
  refresh: () => Promise<void>
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined,
)

const SESSION_STORAGE_KEY = "ff_cache"

/**
 * Drop the last-known-good flags.
 *
 * They are the previous session's answer, and a tab outlives a sign-out: without
 * this, the next person to sign in here keeps them until a refresh succeeds, and
 * a per-user capability flag would light up UI they cannot use. The API resolves
 * flags server-side per user, so this is a wrong-screen problem rather than an
 * access one — but a wrong screen is still worth not showing.
 */
export function clearFeatureFlagCache(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
  } catch {
    // storage unavailable — nothing cached to clear
  }
}

function readCache(): FeatureFlagMap | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as FeatureFlagMap) : null
  } catch {
    return null
  }
}

function writeCache(flags: FeatureFlagMap): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(flags))
  } catch {
    // storage quota or private-mode — non-fatal
  }
}

export const FeatureFlagProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [flags, setFlags] = useState<FeatureFlagMap>(() => {
    const cached = readCache()
    if (cached) {
      setFlagStore(cached)
      return cached
    }
    return DEFAULT_FLAGS
  })
  // Only block render when there is no cache at all (first ever load)
  const [isLoading, setIsLoading] = useState(() => readCache() === null)

  const refresh = useCallback(async () => {
    try {
      const data = await evaluateFeatureFlags()
      // A 2xx response whose body is not the expected shape (e.g. a proxy
      // interstitial) would otherwise put `undefined` into the store, the
      // context and the cache — see FRONTEND-3J. Treat it like a failed
      // request so the catch below keeps the last known good flags.
      if (!data?.flags) {
        throw new Error("feature-flags/evaluate returned no flags")
      }
      setFlagStore(data.flags)
      setFlags(data.flags)
      writeCache(data.flags)
    } catch {
      // Keep existing flags on error so the app remains functional
    } finally {
      setIsLoading(false)
    }
  }, [])

  React.useEffect(() => {
    refresh()
  }, [refresh])

  React.useEffect(() => {
    setFlagRefreshCallback(refresh)
  }, [refresh])

  if (isLoading)
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-grey3" />
    )

  return (
    <FeatureFlagContext.Provider value={{ flags, isLoading, refresh }}>
      {children}
    </FeatureFlagContext.Provider>
  )
}

export const useFeatureFlagContext = () => {
  const context = useContext(FeatureFlagContext)
  if (!context) {
    throw new Error(
      "useFeatureFlagContext must be used within FeatureFlagProvider",
    )
  }
  return context
}

export const useFeatureFlag = (key: FeatureFlagKey): boolean => {
  const context = useContext(FeatureFlagContext)
  return context?.flags?.[key] ?? DEFAULT_FLAGS[key]
}

export const useGetAllFeatureFlags = () => {
  const context = useContext(FeatureFlagContext)
  return context?.flags ?? DEFAULT_FLAGS
}
